import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Ricerca } from './anagrafiche.js';
import { ErroreApi, chiama } from './cliente.js';
import { t } from '../i18n/lingua.js';

/**
 * Trattative.
 *
 * Cinque record veri, di febbraio 2026: il modulo è appena nato. Quello che
 * conta qui non è il volume, è che gli stati abbiano un nome leggibile e che
 * una proposta approvata diventi davvero un listino — la cosa che il v2
 * prometteva su un pulsante e non faceva.
 */

export interface RigaTrattativa {
  id: number;
  customerId: number;
  customer: string | null;
  subject: string;
  status: string;
  budgetAmount: number | null;
  expectedClosingDate: string | null;
  probabilityClosingDeal: number;
  salesmanId: number | null;
  salesman: string | null;
  agentId: number | null;
  agent: string | null;
  activities: number;
  pricelists: number;
  documents: number;
  weightedAmount: number | null;
  archived: boolean;
}

export interface AllegatoTrattativa {
  id: number;
  fileName: string;
  directory: string;
  size: number | null;
}

export interface AttivitaTrattativa {
  id: number;
  activityDate: string;
  activityType: string;
  description: string;
  extendedDescription: string | null;
  attachments: AllegatoTrattativa[];
  archived: boolean;
}

export interface DocumentoTrattativa {
  id: number;
  description: string | null;
  createdAt: string;
  attachments: AllegatoTrattativa[];
  archived: boolean;
}

export interface PropostaListino {
  id: number;
  name: string;
  proposalDate: string;
  startDate: string | null;
  endDate: string | null;
  currency: string;
  shippingMethod: string;
  commercialConditions: string | null;
  status: string;
  approved: boolean;
  approvedById: number | null;
  approvedBy: string | null;
  approvedAt: string | null;
  notes: string | null;
  privateNote: string | null;
  articles: number;
  officialPricelistId: number | null;
  officialPricelistName: string | null;
  /** Vero quando da qui può nascere il listino ufficiale. */
  canGenerate: boolean;
  archived: boolean;
}

export interface Trattativa {
  id: number;
  customerId: number;
  customer: string | null;
  customerBcCode: string | null;
  subject: string;
  status: string;
  budgetAmount: number | null;
  expectedClosingDate: string | null;
  probabilityClosingDeal: number;
  customerNeeds: string | null;
  notes: string | null;
  salesmanId: number | null;
  salesman: string | null;
  agentId: number | null;
  agent: string | null;
  activities: AttivitaTrattativa[];
  documents: DocumentoTrattativa[];
  pricelists: PropostaListino[];
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ArticoloProposta {
  id: number;
  articleId: number;
  sku: string;
  bcNumber: string | null;
  description: string;
  crossReference: string | null;
  priceExw: string;
  costDdp: string;
  discountPercentage1: string | null;
  discountPercentage2: string | null;
  discountPercentage3: string | null;
  taxId: number;
  tax: string | null;
  destinations: {
    id: number;
    addressId: number | null;
    address: string | null;
    city: string | null;
    priceExw: string;
    costDdp: string;
    startDate: string | null;
    endDate: string | null;
  }[];
}

export interface OpzioniTrattative {
  negotiationStates: { value: string; it: string; en: string }[];
  proposalStates: { value: string; it: string; en: string; nota?: string }[];
  proposalTransitions: Record<string, string[]>;
  /** Falso per il commerciale: chiede l'approvazione, non la dà. */
  canApprove: boolean;
  activityTypes: { value: string; it: string; en: string; segno: string }[];
  shippingMethods: readonly string[];
  salesmen: { id: number; label: string }[];
  agents: { id: number; label: string }[];
  taxes: { id: number; label: string; percentage: number; isDefault: boolean }[];
  maxUploadBytes: number;
}

interface Pagina {
  rows: RigaTrattativa[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export const RICERCA_TRATTATIVE: Ricerca = {
  page: 1,
  size: 50,
  // la chiusura più vicina in cima: è la domanda che ci si fa guardando
  // l'elenco — cosa si chiude per primo
  sort: 'expectedClosingDate',
  dir: 'asc',
  q: '',
  archived: false,
  filters: {},
};

function queryString(r: Ricerca, extra: Record<string, string> = {}) {
  return new URLSearchParams({
    page: String(r.page),
    size: String(r.size),
    sort: r.sort,
    dir: r.dir,
    ...(r.q ? { q: r.q } : {}),
    ...(r.archived ? { archived: 'true' } : {}),
    ...Object.fromEntries(
      Object.entries(r.filters ?? {})
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)]),
    ),
    ...extra,
  }).toString();
}

export function useTrattative(ricerca: Ricerca) {
  return useQuery({
    queryKey: ['trattative', ricerca],
    queryFn: () => chiama<Pagina>(`/api/negotiations?${queryString(ricerca)}`),
    placeholderData: (precedente) => precedente,
  });
}

/** Quante per stato, con la ricerca e i filtri dell'elenco tranne lo stato. */
export function useConteggiTrattative(ricerca: Ricerca) {
  const { status: _fuori, ...filtri } = ricerca.filters ?? {};
  const senzaStato = { ...ricerca, filters: filtri, page: 1 };
  return useQuery({
    queryKey: ['trattative-conteggi', senzaStato],
    queryFn: () =>
      chiama<{
        counts: { status: string; label: string; count: number; amount: string; weighted: string }[];
      }>(`/api/negotiations/counts?${queryString(senzaStato)}`),
    placeholderData: (precedente) => precedente,
  });
}

export function useTrattativa(id: number) {
  return useQuery({
    queryKey: ['trattativa', id],
    queryFn: () => chiama<Trattativa>(`/api/negotiations/${id}`),
    enabled: Number.isInteger(id) && id > 0,
  });
}

export function useDefinizioneFiltriTrattative() {
  return useQuery({
    queryKey: ['filtri-trattative'],
    queryFn: () =>
      chiama<{
        filters: {
          key: string;
          label: string;
          type: 'text' | 'list' | 'yesNo' | 'choice' | 'date';
          options?: string;
          values?: { value: string; label: string }[];
        }[];
        options: Record<string, { id: string | number; label: string }[]>;
      }>('/api/negotiations/filters'),
    staleTime: 10 * 60_000,
  });
}

export function useOpzioniTrattative() {
  return useQuery({
    queryKey: ['opzioni-trattative'],
    queryFn: () => chiama<OpzioniTrattative>('/api/negotiations/options'),
    staleTime: 10 * 60_000,
  });
}

export function useArticoliProposta(trattativaId: number, propostaId: number | null) {
  return useQuery({
    queryKey: ['articoli-proposta', trattativaId, propostaId],
    queryFn: () =>
      chiama<{ articles: ArticoloProposta[] }>(
        `/api/negotiations/${trattativaId}/proposals/${propostaId}/articles`,
      ),
    enabled: propostaId !== null,
  });
}

/** Dopo ogni scrittura la scheda si riscrive con quello che torna il servizio. */
function usaScheda(id: number) {
  const client = useQueryClient();
  return (aggiornata: Trattativa) => {
    client.setQueryData(['trattativa', id], aggiornata);
    void client.invalidateQueries({ queryKey: ['trattative'] });
    void client.invalidateQueries({ queryKey: ['trattative-conteggi'] });
  };
}

export function useCreaTrattativa() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<{ id: number }>('/api/negotiations', { metodo: 'POST', corpo: dati }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['trattative'] });
      void client.invalidateQueries({ queryKey: ['trattative-conteggi'] });
    },
  });
}

export function useSalvaTrattativa(id: number) {
  const dopo = usaScheda(id);
  return useMutation({
    mutationFn: (dati: Record<string, unknown>) =>
      chiama<Trattativa>(`/api/negotiations/${id}`, { metodo: 'PATCH', corpo: dati }),
    onSuccess: dopo,
  });
}

export function useSalvaAttivita(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: async ({ id, ...dati }: Record<string, unknown> & { id: number | null }) =>
      id === null
        ? chiama<{ id: number }>(`/api/negotiations/${trattativaId}/activities`, {
            metodo: 'POST',
            corpo: dati,
          }).then(() => chiama<Trattativa>(`/api/negotiations/${trattativaId}`))
        : chiama<Trattativa>(`/api/negotiations/${trattativaId}/activities/${id}`, {
            metodo: 'PATCH',
            corpo: dati,
          }),
    onSuccess: dopo,
  });
}

export function useTogliAttivita(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: (id: number) =>
      chiama<Trattativa>(`/api/negotiations/${trattativaId}/activities/${id}`, {
        metodo: 'DELETE',
      }),
    onSuccess: dopo,
  });
}

export function useSalvaDocumentoTrattativa(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: async ({ id, description }: { id: number | null; description: string | null }) =>
      id === null
        ? chiama<{ id: number }>(`/api/negotiations/${trattativaId}/documents`, {
            metodo: 'POST',
            corpo: { description },
          }).then(() => chiama<Trattativa>(`/api/negotiations/${trattativaId}`))
        : chiama<Trattativa>(`/api/negotiations/${trattativaId}/documents/${id}`, {
            metodo: 'PATCH',
            corpo: { description },
          }),
    onSuccess: dopo,
  });
}

export function useTogliDocumentoTrattativa(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: (id: number) =>
      chiama<Trattativa>(`/api/negotiations/${trattativaId}/documents/${id}`, { metodo: 'DELETE' }),
    onSuccess: dopo,
  });
}

export function useSalvaProposta(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: async ({ id, ...dati }: Record<string, unknown> & { id: number | null }) =>
      id === null
        ? chiama<{ id: number }>(`/api/negotiations/${trattativaId}/proposals`, {
            metodo: 'POST',
            corpo: dati,
          }).then(() => chiama<Trattativa>(`/api/negotiations/${trattativaId}`))
        : chiama<Trattativa>(`/api/negotiations/${trattativaId}/proposals/${id}`, {
            metodo: 'PATCH',
            corpo: dati,
          }),
    onSuccess: dopo,
  });
}

export function useCambiaStatoProposta(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: ({ propostaId, status }: { propostaId: number; status: string }) =>
      chiama<Trattativa>(`/api/negotiations/${trattativaId}/proposals/${propostaId}/status`, {
        metodo: 'POST',
        corpo: { status },
      }),
    onSuccess: dopo,
  });
}

export function useSalvaArticoloProposta(trattativaId: number, propostaId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...dati }: Record<string, unknown> & { id: number | null }) => {
      if (id === null) {
        await chiama<{ id: number }>(
          `/api/negotiations/${trattativaId}/proposals/${propostaId}/articles`,
          { metodo: 'POST', corpo: dati },
        );
      } else {
        await chiama<{ articles: ArticoloProposta[] }>(
          `/api/negotiations/${trattativaId}/proposals/${propostaId}/articles/${id}`,
          { metodo: 'PATCH', corpo: dati },
        );
      }
    },
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['articoli-proposta', trattativaId, propostaId] });
      void client.invalidateQueries({ queryKey: ['trattativa', trattativaId] });
    },
  });
}

export function useTogliArticoloProposta(trattativaId: number, propostaId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      chiama<{ articles: ArticoloProposta[] }>(
        `/api/negotiations/${trattativaId}/proposals/${propostaId}/articles/${id}`,
        { metodo: 'DELETE' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['articoli-proposta', trattativaId, propostaId] });
      void client.invalidateQueries({ queryKey: ['trattativa', trattativaId] });
    },
  });
}

export interface EsitoGenerazione {
  pricelistId: number;
  name: string;
  articles: number;
  destinations: number;
}

export function useGeneraListino(trattativaId: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (propostaId: number) =>
      chiama<EsitoGenerazione>(
        `/api/negotiations/${trattativaId}/proposals/${propostaId}/generate-pricelist`,
        { metodo: 'POST' },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['trattativa', trattativaId] });
      void client.invalidateQueries({ queryKey: ['listini'] });
    },
  });
}

/** L'allegato: multipart, quindi non passa da `chiama`. */
export function useAllegaFileTrattativa(trattativaId: number) {
  const dopo = usaScheda(trattativaId);
  return useMutation({
    mutationFn: async ({
      tipo,
      id,
      file,
    }: {
      tipo: 'activities' | 'documents';
      id: number;
      file: File;
    }) => {
      const dati = new FormData();
      dati.append('file', file);
      const risposta = await fetch(`/api/negotiations/${trattativaId}/${tipo}/${id}/file`, {
        method: 'POST',
        body: dati,
        credentials: 'same-origin',
      });
      const corpo = await risposta.json().catch(() => null);
      if (!risposta.ok) {
        throw new ErroreApi(
          corpo?.error ?? 'erroreCaricamento',
          corpo?.message ?? t('Il caricamento non è riuscito.'),
          risposta.status,
        );
      }
      return corpo as Trattativa;
    },
    onSuccess: dopo,
  });
}

/** Il PDF della proposta: il documento che si manda al cliente. */
export function indirizzoStampaProposta(trattativaId: number, propostaId: number): string {
  return `/api/negotiations/${trattativaId}/proposals/${propostaId}/print`;
}

export function indirizzoAllegato(
  trattativaId: number,
  tipo: 'activity' | 'document',
  allegatoId: number,
): string {
  return `/api/negotiations/${trattativaId}/attachments/${tipo}/${allegatoId}`;
}

export function esportaTrattative(ricerca: Ricerca, formato: 'csv' | 'xlsx') {
  window.location.href = `/api/negotiations?${queryString(ricerca, { format: formato })}`;
}

export function useTrattativeCliente(clienteId: number) {
  return useQuery({
    queryKey: ['trattative-cliente', clienteId],
    queryFn: () =>
      chiama<{ negotiations: RigaTrattativa[]; total: number }>(
        `/api/identities/${clienteId}/negotiations`,
      ),
    enabled: Number.isInteger(clienteId) && clienteId > 0,
  });
}
